import { FC } from "react";
import { Control } from "react-hook-form";

import CommonButton from "../../utilities/formElements/commonButton/CommonButton";
import CommonInput from "../../utilities/formElements/commonInput/CommonInput";
import ClusterPreview from "./ClusterPreview";
import {
  AMBIENT_BARS,
  AMBIENT_GHOSTS,
  AMBIENT_GLOWS,
  AmbientSettings,
} from "../../../util/ambientSettings";
import "./ApplicationPreferences.scss";

/*
 * THE APPLICATION TAB (2026-10-08), extracted from Settings.tsx so it can be laid out and tested on its own.
 *
 * It used to be three Bootstrap columns (col-md-3 / col-md-3 / col-md-4 offset-1) holding: the Bubble Speed
 * radios, two full-width sliders, and two 200x200 previews stacked in the far column. On a wide screen that
 * left the controls hundreds of pixels apart, a ~450px slider per setting, and a block twice as tall as
 * anything in it. Chris, seeing it with the new settings coming: "TOO MUCH white space ... the sliders are way
 * too large."
 *
 * NOW: two cards side by side (stacked below ~1000px).
 *   BUBBLES        speed as a segmented control; each spacing slider sits beside ITS OWN preview.
 *   AMBIENT LIFE   glow and background as chips, five switches in two labelled groups.
 *
 * WHAT WAS NOT CHANGED, on purpose: the speed radios and both sliders are still react-hook-form fields with the
 * same names ("speed", "charge", "historicalCharge"), the same defaults and the same release handlers, so what
 * onSubmit reads is exactly what it read before. Only the markup around them moved. The Ambient controls are
 * plain controlled inputs: they are not form fields, they live in Settings' own state.
 */

interface SpeedOption {
  value: number | string;
  textField: string;
}

interface Props {
  translation: (key: string) => string;
  control: Control<any>;
  speedOptions: SpeedOption[];
  savedSpeedValue: number | string;
  onSpeedChange: (option: SpeedOption) => void;
  /** slider bounds, already as the positive numbers the slider shows */
  sliderMin: number;
  sliderMax: number;
  /** the saved (negative) charge values, shown on the slider as their positive counterparts */
  defaultCharge: number;
  defaultHistoricalCharge: number;
  onPageChargeRelease: (negativeValue: number) => void;
  onHistoricalChargeRelease: (negativeValue: number) => void;
  pageChargePreview: number | undefined;
  historicalChargePreview: number | undefined;
  ambient: AmbientSettings;
  onAmbientChange: (next: AmbientSettings) => void;
  canSave: boolean;
  saving: boolean;
  resetting: boolean;
  onReset: () => void;
}

const Chip: FC<{ name: string; checked: boolean; label: string; onSelect: () => void }> = ({ name, checked, label, onSelect }) => (
  <li>
    <label className="chip">
      <input type="radio" name={name} checked={checked} onChange={onSelect} />
      <span>{label}</span>
    </label>
  </li>
);

const Switch: FC<{ checked: boolean; label: string; hint: string; onToggle: (next: boolean) => void }> = ({ checked, label, hint, onToggle }) => (
  <label className="switch">
    <input type="checkbox" role="switch" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
    <span className="switch__track" aria-hidden="true" />
    <span className="switch__text">
      <b>{label}</b>
      <small>{hint}</small>
    </span>
  </label>
);

const ApplicationPreferences: FC<Props> = ({
  translation: t,
  control,
  speedOptions,
  savedSpeedValue,
  onSpeedChange,
  sliderMin,
  sliderMax,
  defaultCharge,
  defaultHistoricalCharge,
  onPageChargeRelease,
  onHistoricalChargeRelease,
  pageChargePreview,
  historicalChargePreview,
  ambient,
  onAmbientChange,
  canSave,
  saving,
  resetting,
  onReset,
}) => {
  const set = (patch: Partial<AmbientSettings>) => onAmbientChange({ ...ambient, ...patch });

  return (
    <div className="app-prefs">
      <h4 className="app-prefs__title">{t("preferences")}</h4>

      <div className="app-prefs__grid">
        {/* ───────────── BUBBLES ───────────── */}
        <section className="pref-card" aria-labelledby="pref-bubbles">
          <h5 className="pref-card__title" id="pref-bubbles">{t("prefs_bubbles")}</h5>

          <div className="pref-line">
            <h6 id="pref-speed">{t("bubble_speed")}</h6>
            <ul className="seg" role="radiogroup" aria-labelledby="pref-speed" key={String(savedSpeedValue)}>
              {speedOptions.map((speedObject) => (
                <li key={String(speedObject.value)}>
                  <label>
                    <CommonInput
                      className="seg__input"
                      type="radio"
                      control={control as any}
                      name="speed"
                      value={speedObject.value}
                      onChange={() => onSpeedChange(speedObject)}
                      defaultChecked={speedObject.value === savedSpeedValue}
                    />
                    <span className="seg__text">{speedObject.textField}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>

          <div className="spacing">
            <div className="spacing__row">
              <div className="spacing__control">
                <h6 id="pref-page-spacing">{t("bubble_cluster_spacing")}</h6>
                <div className="slider">
                  <span>{t("spacing_tighter")}</span>
                  <CommonInput
                    className="range"
                    aria-labelledby="pref-page-spacing"
                    control={control as any}
                    name="charge"
                    value={defaultCharge * -1}
                    defaultValue={defaultCharge * -1}
                    type="range"
                    min={sliderMin}
                    max={sliderMax}
                    step={1}
                    // Updates the preview once the user releases the knob, rather than a separate
                    // "show preview" click. Reads e.currentTarget.value straight off the DOM, not
                    // through React state, so it can never act on a stale value from before the final
                    // drag position was processed. onTouchEnd covers touch drags, which never fire mouseup.
                    onMouseUp={(e) => onPageChargeRelease(Number(-e.currentTarget.value))}
                    onTouchEnd={(e) => onPageChargeRelease(Number(-e.currentTarget.value))}
                  />
                  <span>{t("spacing_looser")}</span>
                </div>
              </div>
              <div className="spacing__preview" aria-hidden="true">
                <ClusterPreview charge={pageChargePreview ? pageChargePreview : defaultCharge} />
              </div>
            </div>

            <div className="spacing__row">
              <div className="spacing__control">
                <h6 id="pref-hist-spacing">{t("historical_cluster_spacing")}</h6>
                <div className="slider">
                  <span>{t("spacing_tighter")}</span>
                  <CommonInput
                    className="range"
                    aria-labelledby="pref-hist-spacing"
                    control={control as any}
                    name="historicalCharge"
                    value={defaultHistoricalCharge * -1}
                    defaultValue={defaultHistoricalCharge * -1}
                    type="range"
                    min={sliderMin}
                    max={sliderMax}
                    step={1}
                    onMouseUp={(e) => onHistoricalChargeRelease(Number(-e.currentTarget.value))}
                    onTouchEnd={(e) => onHistoricalChargeRelease(Number(-e.currentTarget.value))}
                  />
                  <span>{t("spacing_looser")}</span>
                </div>
              </div>
              <div className="spacing__preview" aria-hidden="true">
                <ClusterPreview
                  variant="historical"
                  charge={historicalChargePreview ? historicalChargePreview : defaultHistoricalCharge}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ───────────── AMBIENT LIFE ───────────── */}
        <section className="pref-card" aria-labelledby="pref-ambient">
          <h5 className="pref-card__title" id="pref-ambient">{t("ambient_life")}</h5>
          <p className="pref-card__sub">{t("ambient_life_note")}</p>

          <div className="choice">
            <h6 id="pref-glow">{t("ambient_glow")}</h6>
            <ul className="chips" role="radiogroup" aria-labelledby="pref-glow">
              {AMBIENT_GLOWS.map((value) => (
                <Chip key={value} name="ambientGlow" checked={ambient.ambientGlow === value} label={t(`ambient_glow_${value}`)} onSelect={() => set({ ambientGlow: value })} />
              ))}
            </ul>
          </div>

          <div className="choice">
            <h6 id="pref-ghost">{t("ambient_behind")}</h6>
            <ul className="chips" role="radiogroup" aria-labelledby="pref-ghost">
              {AMBIENT_GHOSTS.map((value) => (
                <Chip key={value} name="ambientGhost" checked={ambient.ambientGhost === value} label={t(`ambient_ghost_${value}`)} onSelect={() => set({ ambientGhost: value })} />
              ))}
            </ul>
          </div>

          <div className="choice">
            <h6 id="pref-bar">{t("ambient_bar")}</h6>
            <ul className="chips" role="radiogroup" aria-labelledby="pref-bar">
              {AMBIENT_BARS.map((value) => (
                <Chip key={value} name="ambientBar" checked={ambient.ambientBar === value} label={t(`ambient_bar_${value}`)} onSelect={() => set({ ambientBar: value })} />
              ))}
            </ul>
          </div>

          <div className="switches">
            <div className="switches__col">
              <h6>{t("ambient_also_moving")}</h6>
              <Switch checked={ambient.ambientDock} label={t("ambient_dock")} hint={t("ambient_dock_hint")} onToggle={(v) => set({ ambientDock: v })} />
              <Switch checked={ambient.ambientCounter} label={t("ambient_counter")} hint={t("ambient_counter_hint")} onToggle={(v) => set({ ambientCounter: v })} />
            </div>
            <div className="switches__col">
              <h6>{t("ambient_time_group")}</h6>
              <Switch checked={ambient.ambientTimeLight} label={t("ambient_time_light")} hint={t("ambient_time_light_hint")} onToggle={(v) => set({ ambientTimeLight: v })} />
              <Switch checked={ambient.ambientGreeting} label={t("ambient_greeting")} hint={t("ambient_greeting_hint")} onToggle={(v) => set({ ambientGreeting: v })} />
            </div>
          </div>
        </section>
      </div>

      <div className="app-prefs__actions">
        <CommonButton
          type="submit"
          label={t("save_preferences")}
          className="theme-btn success-btn btn-sm"
          loading={saving}
          disabled={!canSave || saving}
        />
        <CommonButton
          type="button"
          label={t("reset")}
          className="theme-btn white-btn btn-sm"
          loading={resetting}
          disabled={resetting}
          onClick={onReset}
        />
      </div>
    </div>
  );
};

export default ApplicationPreferences;
